import { useState } from 'react';
import { ArrowRight, Facebook, ShieldCheck, UserRound, Wrench, KeyRound } from 'lucide-react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';

function roleFromPath(path) {
  if (path.startsWith('/login/admin')) return 'admin';
  if (path.startsWith('/login/technician')) return 'technician';
  return 'customer';
}
function roleLabel(role) { return role === 'admin' ? 'Admin' : role === 'technician' ? 'Technician' : 'Customer'; }
function destination(role) { return role === 'technician' ? '/technician' : role === 'admin' ? '/admin' : '/portal'; }

export default function LoginPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const role = roleFromPath(loc.pathname);
  const { login, register, oauth, resetPassword, supabaseConfigured } = useAuth();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async e => {
    e.preventDefault(); setError(''); setBusy(true);
    try {
      if (mode === 'forgot') {
        await resetPassword(email);
        setError('Password reset email sent. Check your inbox.');
      } else if (mode === 'login') {
        const u = await login({ email, password });
        const expected = role === 'admin' ? ['admin','operations','finance','support','super_admin'] : [role];
        if (!expected.includes(u.role)) throw new Error(`This account is not authorized for ${roleLabel(role).toLowerCase()} access.`);
        nav(destination(role), { replace: true });
      } else {
        if (role !== 'customer') throw new Error('Registration is available only from the customer login.');
        await register({ email, password, name, phone });
        setMode('login'); setError('Account created. Check your email if confirmation is enabled.');
      }
    } catch (e2) { setError(e2.message || 'Unable to continue.'); }
    finally { setBusy(false); }
  };

  return <section className="login-page">
    <div className="login-card">
      <div className="login-brand">KIZAK<span>®</span></div>
      <div className="login-role-icon">{role === 'admin' ? <ShieldCheck/> : role === 'technician' ? <Wrench/> : <UserRound/>}</div>
      <h1>{mode === 'forgot' ? 'Reset password' : mode === 'register' ? 'Create customer account' : `${roleLabel(role)} sign in`}</h1>
      <p>{role === 'customer' ? 'Customer storefront and service access.' : role === 'admin' ? 'Operations and administration access.' : 'Field service access.'}</p>
      <form onSubmit={submit}>
        {mode === 'register' && <><label>Name<input value={name} onChange={e=>setName(e.target.value)} required/></label><label>Phone<input value={phone} onChange={e=>setPhone(e.target.value)} required/></label></>}
        <label>Email<input value={email} onChange={e=>setEmail(e.target.value)} type="email" required/></label>
        {mode !== 'forgot' && <label>Password<input value={password} onChange={e=>setPassword(e.target.value)} type="password" minLength={8} required/></label>}
        {error && <div className="form-error">{error}</div>}
        <button className="outline-btn" type="submit" disabled={busy}>{mode === 'forgot' ? <KeyRound size={16}/> : <ArrowRight size={16}/>} {busy ? 'Please wait…' : mode === 'forgot' ? 'Send reset email' : mode === 'register' ? 'Create account' : 'Continue'} <ArrowRight size={16}/></button>
      </form>
      {mode === 'login' && role === 'customer' && <>
        <button className="login-switch" type="button" onClick={()=>setMode('forgot')}>Forgot password?</button>
        <div className="login-divider"><span>or continue with</span></div>
        <div className="social-login"><button type="button" onClick={()=>oauth('google')}><b className="google-g">G</b> Google</button><button type="button" onClick={()=>oauth('facebook')}><Facebook size={16}/> Facebook</button></div>
        <button className="login-switch" type="button" onClick={()=>setMode('register')}>Create a customer account</button>
      </>}
      {mode !== 'login' && <button className="login-switch" type="button" onClick={()=>setMode('login')}>Back to sign in</button>}
      <div className="login-app-links"><Link to="/login">Customer</Link><Link to="/login/admin">Admin</Link><Link to="/login/technician">Technician</Link></div>
      {!supabaseConfigured && <div className="form-error">Configure Supabase before using authentication.</div>}
    </div>
  </section>;
}
